(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Lightbox = global.NewHomeSource.Lightbox || {};

  const VideoPlayers = global.NewHomeSource.VideoPlayers;

  if (!VideoPlayers ||
     !VideoPlayers.InternalProvider ||
     !VideoPlayers.Youtube ||
     !VideoPlayers.Vimeo
  ) {
    return;
  }

  class VideosHandler {
    /**
     * Initializes a new instance of the class.
     * Sets up initial state, video players, index, and event observers.
     */
    constructor() {
      this.state = null;
      this.videos = {};
      this.videoPlayers = {
        internalprovider: VideoPlayers.InternalProvider,
        youtube: VideoPlayers.Youtube,
        vimeo: VideoPlayers.Vimeo,
      };
      this.currentIndex = null;
      this.eventsToObserve = {
        itemsChange: this.itemsChange.bind(this),
      };
    }

    /**
     * Initializes the videos handler with the provided state and sets up observers.
     * @param {Object} state - The initial state to be used by the handler.
     * @returns {this} The instance of the videos handler for chaining.
     */
    initialize(state) {
      this.state = state;
      this.addObservers();
      return this;
    }

    /**
     * Retrieves the video player instance associated with the specified video type.
     *
     * @param {string} videoType - The type of video player to find (case-insensitive).
     * @returns {*} The video player instance corresponding to the given video type, or undefined if not found.
     */
    findPlayer(videoType) {
      return this.videoPlayers[videoType.toLowerCase()];
    }

    /**
     * Retrieves video slice elements from the DOM based on the provided column indexes.
     *
     * @param {number[]} indexes - An array of column indexes to select video slices for.
     * @returns {Element[]} An array of DOM elements corresponding to the specified column indexes.
     */
    getVideoSlices(indexes) {
      const rootNode = this.state.get('rootNode');
      return indexes.map((index) => rootNode.querySelector(`[data-column="${index}"][data-video-id]`))
        .filter(Boolean);
    }

    /**
     * Handles changes to the video items, updating the current index, pausing all videos,
     * creating new video elements for the specified indexes, and playing the selected video.
     *
     * @async
     * @param {Object} data - The data object containing information about the current selection.
     * @param {number[]} [data.indexes] - Array of indexes representing the selected video slices.
     * @param {number} data.currentIndex - The index of the currently selected video.
     * @returns {Promise<void>} Resolves when the video elements have been updated and the selected video is playing.
     */
    async itemsChange(data) {
      const indexes = data.indexes || [];
      if (this.currentIndex === data.currentIndex) {
        return;
      }
      this.currentIndex = data.currentIndex;
      Object.values(this.videos).forEach((video) => video.pause());
      const slices = this.getVideoSlices(indexes);
      const videos = await Promise.all(slices.map(this.createVideo.bind(this)));
      this.setVideos(videos);
      this.playCurrentVideo();
    }

    /**
     * Plays the video at the current index in the videos array, if it exists.
     */
    playCurrentVideo() {
      if (this.videos[this.currentIndex]) {
        this.videos[this.currentIndex].play();
      }
    }

    /**
     * Updates the internal videos collection by mapping each video's index to its video object.
     * Existing videos are preserved and new/updated videos are merged in.
     *
     * @param {Array<{index: string|number, video: any}>} videos - Array of video objects, each containing an
     * 'index' and 'video' property.
     */
    setVideos(videos) {
      this.videos = videos.reduce((videoCollection, currentVideo) => Object.assign(
        {},
        videoCollection,
        { [currentVideo.index]: currentVideo.video }
      ), this.videos);
    }

    /**
     * Asynchronously creates and initializes a video player instance within a specified container.
     *
     * @param {HTMLElement} element - The DOM element containing data attributes for video type and ID.
     * @param {HTMLElement} container - The DOM element where the video player will be inserted.
     * @returns {Promise<Object>} A promise that resolves to the initialized video player instance.
     */
    async createVideo(slice) {
      return new Promise((resolve) => {
        const index = parseInt(slice.dataset.column, 10);
        const container = slice.querySelector('[data-component="lightbox-video-container"]');
        if (this.videos[index]) {
          resolve({
            video: this.videos[index],
            index
          });
          return;
        }

        const videoType = slice.dataset.videoType;
        const videoId = slice.dataset.videoId;
        const mediaId = slice.dataset.mediaId;
        const thumbnailUrl = slice.dataset.thumbnailUrl;
        const videoUrl = slice.dataset.videoUrl;
        const Player = this.findPlayer(videoType);
        const video = new Player();
        const handler = { };

        const videoElement = Player.createContainer(videoId, mediaId);
        container.prepend(videoElement);
        video.initialize(videoId, handler, mediaId, videoUrl, thumbnailUrl);
        video.createVideo().then(() => resolve({
          video,
          index
        }));
      });
    }

    /**
     * Cleans up video components by disposing of each video instance,
     * clearing their associated DOM containers, resetting the videos object,
     * and removing any observers.
     */
    destroy() {
      const rootNode = this.state.get('rootNode');
      Object.keys(this.videos).forEach((index) => {
        this.videos[index].dispose();
        const element = rootNode.querySelector(`[data-column="${index}"]`);
        const container = element.querySelector('[data-component="lightbox-video-container"]');
        container.innerHTML = '';
      });
      this.videos = {};
      this.removeObservers();
    }

    /**
     * Registers this instance as an observer for each event specified in `eventsToObserve`.
     * Iterates over all event keys and adds this instance as an observer to the state for each event.
     */
    addObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.addObserver(event, this));
    }

    /**
     * Removes this instance as an observer from all events listed in `eventsToObserve`.
     * Iterates over each event and calls `removeObserver` on the state object.
     */
    removeObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.removeObserver(event, this));
    }

    /**
     * Updates the carousel by invoking the callback associated with the given topic.
     *
     * @param {*} data - The data to pass to the callback function.
     * @param {string} topic - The topic whose callback should be invoked.
     */
    update(data, topic) {
      const callback = this.eventsToObserve[topic];
      if (callback) {
        callback(data);
      }
    }
  }

  global.NewHomeSource.Lightbox.VideosHandler = VideosHandler;
}(window));
